import { inject, Injectable } from '@angular/core';
import { ApmService } from '@elastic/apm-rum-angular';
import { Transaction } from '@elastic/apm-rum';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { ThemeService } from '../_theme/theme.service';
import { environment } from '../../../environments/environment';
import { isLocalhost } from '../../../main';

@Injectable({
  providedIn: 'root',
})
export class ApmEventsService {
  private apmService = inject(ApmService);
  private themeService = inject(ThemeService);

  /**
   * Initialize the APM service if enabled in the theme configuration.
   * This method sets up the APM service with the service name, server URL, and
   * environment name from the theme configuration.
   * It should be called once at the application startup to ensure that
   * the APM service is ready to capture events and errors.
   */
  init(): void {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return;
    }

    // Global listener for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.apmService?.apm?.captureError(event.reason || 'Unhandled promise rejection');
    });

    // Agent API is exposed through this apm instance
    this.apmService?.apm?.init({
      serviceName: ThemeConfig.apmServiceName,
      serverUrl: environment.apmServerUrl,
      serviceVersion: this.themeService.getLocalVersion(true),
      environment: ThemeConfig.environmentName,
    });
  }

  /**
   * Set the user context for APM.
   * This method retrieves the current company and user data from local storage
   * and sets the user context in the APM service.
   * It should be called whenever the user context changes, such as after login or logout.
   */
  setUserContext(): void {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return;
    }

    // Initialize the APM service
    const currentCompany = JSON.parse(localStorage.getItem('companySelected'));
    const userData = JSON.parse(localStorage.getItem('auth_tokens'));

    if (currentCompany && userData) {
      // Set the service name based on the selected company
      this.apmService?.apm?.setUserContext({
        username: userData?.username || null,
        id: currentCompany?.id || null,
      });

      this.addApmLabels({
        user_roles: userData?.roles?.join(',') || 'guest',
        company_fields: currentCompany?.fields?.length || 0,
        company_fieldsSize: currentCompany?.fieldsSize || 0,
        company_preferredCountry: currentCompany?.preferredCountry || '',
        company_preferredProductsCountry: currentCompany?.preferredProductsCountry || '',
      });
    }
  }

  /**
   * Set the APM labels for additional context.
   * This method retrieves relevant information (e.g., browser, locale, plan, company name)
   * and sets them as labels in the APM service.
   * It should be called whenever the application context changes
   * (e.g., after login, company switch, or environment change).
   */
  setInitialApmLabels(): void {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return;
    }

    const labels: Record<string, string | number | boolean> = {
      locale: localStorage.getItem('crntLng') || 'default',
      appVersion: this.themeService.getLocalVersion(true),
    };

    // Remove null/undefined values to avoid warnings in APM
    Object.keys(labels).forEach((key) => {
      if (labels[key] === null || labels[key] === undefined) {
        delete labels[key];
      }
    });

    this.apmService?.apm?.addLabels(labels);
  }

  /**
   * Add or update APM labels dynamically.
   * This can be used multiple times – labels will be merged (same keys overwrite).
   * @param extraLabels - key/value pairs to be added as APM labels
   */
  addApmLabels(extraLabels: Record<string, string | number | boolean>): void {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return;
    }

    // Remove null/undefined values
    Object.keys(extraLabels).forEach((key) => {
      if (extraLabels[key] === null || extraLabels[key] === undefined) {
        delete extraLabels[key];
      }
    });

    this.apmService?.apm?.addLabels(extraLabels);
  }

  /**
   * Reset the user context in APM.
   * This method clears the user context in the APM service, typically used when
   * the user logs out or when the application needs to reset the user state.
   */
  resetUserContext(): void {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return;
    }

    // Reset the user context in APM
    this.apmService?.apm?.setUserContext({
      username: null,
      id: null,
    });
  }

  /**
   * Start a custom transaction in APM.
   * @param name - The name of the transaction (e.g., 'splash-screen')
   * @param type - Optional type (default: 'custom')
   */
  startCustomTransaction(name: string, type: string = 'custom'): Transaction {
    if (!ThemeConfig.enableApm || isLocalhost) {
      return null;
    }
    return this.apmService?.apm?.startTransaction(name, type);
  }

  /**
   * End a custom transaction.
   * @param transaction - The transaction object returned by startCustomTransaction
   */
  endCustomTransaction(transaction: Transaction) {
    if (!ThemeConfig.enableApm || !transaction) {
      return;
    }
    transaction.end();
  }
}
